import { useEffect, useState } from 'react';
import { BarChart3, Boxes, Hammer, PackageMinus, PackagePlus, Search } from 'lucide-react';
import { Button, Input } from '@eombor/ui';
import { FLOW_PAGE_SIZE, type FlowSort, type ReportRange } from '@eombor/contracts';
import { t } from '../../../shared/i18n/index.js';
import { PageHeader } from '../../../shared/ui/PageHeader.js';
import { Segmented } from '../../../shared/ui/Segmented.js';
import { DEFAULT_RANGE, RangePicker } from '../../dashboard/ui/RangePicker.js';
import { MetricCard } from '../../dashboard/ui/MetricCard.js';
import { ReportCard } from '../../dashboard/ui/ReportCard.js';
import { useFlowReport } from '../model/flow.queries.js';
import type { FlowNode, FlowTarget } from '../api/flow.api.js';
import { FlowCrumbs } from './FlowCrumbs.js';
import { FlowDetailPanel } from './FlowDetailPanel.js';
import { FlowTable } from './FlowTable.js';

/**
 * STATISTIKA — korxona boshlig'i, MTU rahbari va administratori uchun.
 *
 * Savol bitta: «bo'linmalarim qancha mol oldi va qanchasini hisobdan
 * chiqardi». Javob tuzilmaning qaysi qatlamida turganiga qarab boshqa:
 * MTU → korxona → bo'linma → ichki ombor.
 *
 * Boshlang'ich daraja SERVERDAN keladi: doira kimga nimani ko'rsatishni
 * o'zi hal qiladi va sahifada «men kimman» degan shart yo'q. Shu sababli
 * `node` boshida `null` — birinchi so'rov darajani ham so'raydi.
 *
 * Qidiruv, saralash va sahifa ham SERVERDA: bitta MTU da mingdan ortiq
 * korxona, korxonada yuzdan ortiq bo'linma bo'lishi mumkin. Mijozda
 * saralash «eng faol» o'rniga «shu yigirmatasining eng faoli» ni
 * ko'rsatardi — ya'ni yolg'on reyting.
 */
export const StatisticsPage = () => {
  const [range, setRange] = useState<ReportRange>(DEFAULT_RANGE);
  const [node, setNode] = useState<FlowNode | null>(null);
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<FlowSort>('name');
  const [page, setPage] = useState(1);
  /** Ochiq panelning tuguni — `null` bo'lsa panel yopiq. */
  const [target, setTarget] = useState<FlowTarget | null>(null);

  const query = useFlowReport(range, node, { q, sort, page });
  const report = query.data;
  const series = report?.series ?? [];
  const total = report?.total ?? 0;

  /*
   * Har qanday o'zgarish sahifani BIRINCHISIGA qaytaradi.
   *
   * Aks holda uchinchi sahifada turib qidiruv yozgan odam bo'sh ro'yxat
   * ko'rardi — topilgan ikkita satr birinchi sahifada qolgan bo'lardi va
   * ekran «hech narsa topilmadi» deb yolg'on gapirardi.
   */
  useEffect(() => {
    setPage(1);
  }, [q, sort, node, range]);

  /** Pastga tushganda qidiruv TOZALANADI — u oldingi ro'yxatniki edi. */
  const drill = (next: FlowNode): void => {
    setNode(next);
    setQ('');
    /* Panel oldingi darajaning tuguniga tegishli edi — pastga
       tushganda u ekranda qolib, boshqa ro'yxat ustida turardi. */
    setTarget(null);
  };

  const first = (page - 1) * FLOW_PAGE_SIZE + 1;
  const last = Math.min(page * FLOW_PAGE_SIZE, total);
  const lastPage = Math.max(1, Math.ceil(total / FLOW_PAGE_SIZE));

  return (
    <div className="flex flex-col gap-5">
      <PageHeader title={t('stats.title')} description={t('stats.subtitle')} />

      <div className="flex flex-wrap items-end justify-between gap-4">
        {/*
          Nafas chizig'i ko'rsatkichlardan OLDIN: raqamlar «qayerning»
          raqami ekani ularni o'qishdan avval bilinishi kerak.
        */}
        {report === undefined ? (
          <span className="text-text-faint text-sm">{t('stats.loadingPath')}</span>
        ) : (
          <FlowCrumbs crumbs={report.breadcrumb} onGo={drill} />
        )}
        <RangePicker value={range} onChange={setRange} />
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          icon={PackagePlus}
          tone="success"
          title={t('stats.in')}
          hint={t('stats.inHint')}
          loading={query.isPending}
          value={report === undefined ? null : report.totals.inDocCount.toLocaleString('ru-RU')}
          spark={series.map((point) => point.inDocCount)}
        />
        <MetricCard
          icon={PackageMinus}
          tone="info"
          title={t('stats.out')}
          hint={t('stats.outHint')}
          loading={query.isPending}
          value={report === undefined ? null : report.totals.outDocCount.toLocaleString('ru-RU')}
          spark={series.map((point) => point.outDocCount)}
        />
        {/*
          ISHLATISHDA — «hozir kim ishlatyapti» degan savolning javobi.
          Partiya bo'yicha sanaladi: tugun ichida dona, metr va kilogramm
          aralash turadi va ularning yig'indisi hech narsani anglatmaydi.
          Davri yo'q — bu HOZIRGI holat, shuning uchun chizig'i ham yo'q.
        */}
        <MetricCard
          icon={Hammer}
          tone="warn"
          title={t('stats.inUse')}
          hint={
            report === undefined
              ? t('stats.inUseHint')
              : t('stats.inUseWorkers', { count: report.totals.usageWorkerCount })
          }
          loading={query.isPending}
          value={report === undefined ? null : report.totals.inUseLotCount.toLocaleString('ru-RU')}
        />
        {/*
          Qoldiq DAVRSIZ — «hozir nima turibdi» degan savolning o'tgan oy
          bilan aloqasi yo'q. Shuning uchun grafigi ham yo'q: chiziq
          o'zgarmas sonning tagida yolg'on dinamika chizardi.
        */}
        <MetricCard
          icon={Boxes}
          tone="neutral"
          title={t('stats.lots')}
          hint={t('stats.lotsHint')}
          loading={query.isPending}
          value={report === undefined ? null : report.totals.lotCount.toLocaleString('ru-RU')}
        />
      </div>

      <ReportCard
        icon={BarChart3}
        tone="accent"
        title={t('stats.tableTitle')}
        /* Sarlavha ostidagi qator ro'yxat QANCHALIGINI aytadi: usiz
           birinchi yigirmata satr butun ro'yxatdek ko'rinardi va odam
           qidiruvga umuman qo'l urmasdi. */
        hint={total === 0 ? t('stats.tableHint') : t('stats.shown', { first, last, total })}
        pending={query.isPending}
        error={query.error ?? null}
        empty={report !== undefined && report.rows.length === 0}
        emptyText={q === '' ? t('stats.empty') : t('stats.notFound')}
        actions={
          <div className="flex flex-wrap items-center gap-3">
            <Input
              variant="filter"
              type="search"
              aria-label={t('stats.search')}
              placeholder={t('stats.search')}
              prefix={<Search size={16} />}
              value={q}
              onChange={(event) => setQ(event.target.value)}
            />
            <Segmented
              label={t('stats.sort')}
              value={sort}
              onChange={setSort}
              options={[
                { value: 'name', label: t('stats.sortName') },
                { value: 'in', label: t('stats.sortIn') },
                { value: 'out', label: t('stats.sortOut') },
              ]}
            />
          </div>
        }
      >
        {report === undefined ? null : (
          <div className="flex flex-col gap-3">
            <FlowTable
              level={report.level}
              rows={report.rows}
              onDrill={drill}
              onDetail={(row) =>
                setTarget({
                  level: report.level,
                  id: row.id,
                  name: row.name,
                  drillLevel: row.drillLevel,
                })
              }
            />

            {/* Bitta sahifaga sig'sa, boshqaruv umuman chizilmaydi. */}
            {lastPage === 1 ? null : (
              <nav
                aria-label={t('stats.pages')}
                className="flex items-center justify-between gap-3"
              >
                <p className="text-text-faint text-xs">
                  {t('stats.pageOf', { page, last: lastPage })}
                </p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={page <= 1}
                    onClick={() => setPage((current) => current - 1)}
                  >
                    {t('stats.prev')}
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={page >= lastPage}
                    onClick={() => setPage((current) => current + 1)}
                  >
                    {t('stats.next')}
                  </Button>
                </div>
              </nav>
            )}
          </div>
        )}
      </ReportCard>

      <FlowDetailPanel
        range={range}
        target={target}
        onClose={() => setTarget(null)}
        onDrill={drill}
      />
    </div>
  );
};
